import React, { useState, useCallback, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import dayjs from 'dayjs';

import { ApiServiceServerless } from '../../../xhr_libs';
import Bill from '../../../components/bills/Bill';
import EmptyBill from '../../../components/bills/EmptyBill';

import CsvUploadContainer from './CsvUploadContainer';
import EnergyTracerTable from '../../../components/tables';
import ChartInfoPopover from '../../../components/chartElements/Actions/ChartInfoPopover';

const styles = {
  column: {
    paddingTop: '11px',
  },
};

const useEmptyBill = (type, defaultConsumptionUnit, allBills) => {
  const createEmptyBill = useCallback(
    () =>
      EmptyBill(
        type,
        defaultConsumptionUnit,
        typeof allBills !== 'undefined' && typeof allBills[0] !== 'undefined'
          ? allBills[0].read_date
          : null
      ),
    [type, defaultConsumptionUnit, allBills]
  );

  const [emptyBill, setEmptyBill] = useState(createEmptyBill());

  useEffect(() => {
    setEmptyBill(createEmptyBill());
  }, [createEmptyBill]);

  return emptyBill;
};

export default function AddBills(props) {
  const {
    type,
    defaultConsumptionUnit,
    account,
    allBills,
    isLoadingAccount,
    setIsSaving,
    showToast,
    handleRefreshBills,
    handleAddNewBill,
    validateBill,
    setSelectedResource,
    resetBuilding,
    handleNewMaxDate,
  } = props;

  const emptyBill = useEmptyBill(type, defaultConsumptionUnit, allBills);

  const [recentBills, setRecentBills] = useState([]);

  const saveNewBill = (data, exit) => {
    setIsSaving(true);
    let send_data = { ...data };
    delete send_data.provider_bill_type;
    delete send_data.provider_bill_id;
    ApiServiceServerless.post(
      `/bills`,
      { ...send_data, account_id: account.id },
      { authorization_id: account.id }
    )
      .then((res) => {
        handleAddNewBill(res.data);
        handleNewMaxDate({
          account_id: account.id,
          new_max_date: res.data.read_date,
        });
        const message = 'Bill has been created.';
        showToast('success', 'Success', message);
        setIsSaving(false);
        if (exit) {
          setSelectedResource('organization');
          resetBuilding();
        }
      })
      .catch((error) => {
        showToast('danger', 'Error', error);
        setIsSaving(false);
        throw error;
      });
  };

  useEffect(() => {
    let recent_bills = allBills.filter((bill) => bill.provider_meter_id === 'none').slice(0, 24)

    for (let i = 0; i < recent_bills.length - 1; i++) {
      recent_bills[i].start_date_warning = checkDates(recent_bills[i], recent_bills[i + 1], 'start_date')
      recent_bills[i].read_date_warning = checkDates(recent_bills[i], recent_bills[i + 1], 'read_date')
      recent_bills[i].bill_date_warning = checkDates(recent_bills[i], recent_bills[i + 1], 'bill_date')
    }
    setRecentBills(recent_bills)
  }, [
    allBills
  ])

  const checkDates = (bill1, bill2, key) => {
    let d1 = dayjs.utc(bill1[key])
    let d2 = dayjs.utc(bill2[key])

    let c1 = d1.year() * 12 + d1.month()
    let c2 = d2.year() * 12 + d2.month()

    return c1 - c2 > 1 || c1 - c2 === 0 ? true : false
  }


  const tableColumns = [
    {
      dataField: 'start_date',
      text: 'Start Date',
      style: styles.column,
      formatter: (cell, row) =>
        <div style={{ 'color': row.start_date_warning ? 'red' : 'black' }}>{cell
          ? dayjs.utc(cell).format('YYYY-MM-DD')
          : ''}</div>
    },
    {
      dataField: 'read_date',
      text: 'Read Date',
      style: styles.column,
      formatter: (cell, row) =>
        <div style={{ 'color': row.read_date_warning ? 'red' : 'black' }}>
          {cell
            ? dayjs.utc(cell).format('YYYY-MM-DD')
            : ''}</div>
    },
    {
      dataField: 'bill_date',
      text: 'Utility Month',
      style: styles.column,
      formatter: (cell, row) =>
        <div style={{ 'color': row.bill_date_warning ? 'red' : 'black' }}>{cell
          ? dayjs.utc(cell).format('YYYY-MM')
          : ''}</div>
    },
    {
      dataField: 'total_consumption',
      text: 'Total Consumption',
      style: styles.column,
      formatter: (cell) => <>{cell
        ? cell.toLocaleString()
        : ''}</>
    },
    {
      dataField: 'total_cost',
      text: 'Total Cost',
      style: styles.column,
      formatter: (cell) => <>{cell
        ? cell.toLocaleString()
        : ''}</>
    },
  ];


  return (
    <>
      <Row>
        <Col md={9}>
          <div
            style={{
              border: '1px solid rgba(0, 0, 0, 0.1)',
              padding: '0 0 1em 1em',
            }}
          >
            {emptyBill && (
              <Bill
                data={emptyBill}
                account={account}
                allBills={allBills}
                saveBill={saveNewBill}
                validateBill={validateBill}
                isTotal={false}
                updateAccess={true}
                addToSaveBills={() => { }}
                hideBottomBorder={true}
              />
            )}

            {recentBills.length > 0 &&
              <>
                <h4>Recent Bills <ChartInfoPopover title={'Bill Warning'} body={'Red dates below signify either a gap month between the previous date or that the date is in the same month as the previous'} /></h4>
                <EnergyTracerTable data={recentBills} columns={tableColumns} enableScroll={true} wrapperStyle={'et-table-wrapper-xs'} /></>}

          </div>
        </Col>
        <Col md={3}>
          <CsvUploadContainer
            isLoadingAccount={isLoadingAccount}
            account={account}
            showToast={showToast}
            handleRefreshBills={handleRefreshBills}
          />

          <br></br>
        </Col>
      </Row>
    </>
  );
}
